import axios from 'axios';
import { getAuthToken } from './apiClient';
import config from '../config';

const getBaseURL = () => {
  if (!config.baseApi) {
    throw new Error('REACT_APP_BASE_URL is not defined');
  }
  return config.baseApi;
};

/**
 * Uploads an image file to the server
 * @param {File} file - The image file to upload
 * @param {{ folder?: string }} [options] - Optional upload options (e.g. folder path)
 * @returns {Promise<string>} - The uploaded image URL
 * @throws {Error} - If upload fails
 */
export const uploadImage = async (file, options = {}) => {
  if (!file) {
    throw new Error('No file provided');
  }

  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  return uploadFile(file, options);
};

/**
 * Uploads any file to the server
 * @param {File} file - The file to upload
 * @param {{ folder?: string }} [options] - Optional upload options (e.g. folder path)
 * @returns {Promise<string>} - The uploaded file URL
 * @throws {Error} - If upload fails
 */
export const uploadFile = async (file, options = {}) => {
  if (!file) {
    throw new Error('No file provided');
  }

  // // Validate file size (5MB)
  // if (file.size > 5 * 1024 * 1024) {
  //   throw new Error('File size must be less than 5MB');
  // }

  // Get authorization token
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authorization token is missing. Please log in again.');
  }

  // Create FormData for multipart/form-data
  const formData = new FormData();
  formData.append('file', file);
  if (options.folder) {
    formData.append('folder', options.folder);
  }

  try {
    const response = await axios.post(`${getBaseURL()}/api/v1/assets/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`,
      },
      timeout: 60000, // 60 seconds timeout for large files
    });

    // Extract URL from response
    // Adjust based on actual API response structure
    const fileUrl = response.data?.data?.url ||
      response.data?.url ||
      response.data?.image_url ||
      response.data?.thumbnail_url ||
      response.data;

    if (!fileUrl || typeof fileUrl !== 'string') {
      throw new Error('Invalid response: File URL not found in response');
    }

    return fileUrl;
  } catch (error) {
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data?.message ||
        error.response.data?.error ||
        `Upload failed: ${error.response.status} ${error.response.statusText}`;
      throw new Error(errorMessage);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Network error: Unable to connect to server. Please check your internet connection.');
    } else {
      // Something else happened
      throw new Error(error.message || 'Failed to upload file');
    }
  }
};
